/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { SerializedError } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../../ts';
import { Snapshot, SnapshotsSkill } from '../../../../ts/snapshot';
import { RootState } from '../../../../ts/state';
import {
  selectActionSkillId,
  selectDialogSkillId
} from '../assistants/core-selectors';
import { entityAdapter } from './adapter';

const selectors = entityAdapter.getSelectors(
  (state: RootState) => state.snapshotsSkills
);

/**
 * Return true if all the skill snapshots were loaded successfully
 */
export const selectSkillSnapshotById = (
  state: RootState,
  skillId: string
): SnapshotsSkill | undefined => selectors.selectById(state, skillId);

/**
 * Return true if all the skill snapshots were loaded successfully
 */
export const selectSnapshotFetchedBySkillId = (
  state: RootState,
  skillId: string
): number | undefined => {
  return state?.snapshotsSkills?.listFetched?.[skillId];
};

/**
 * Return true if any skill snapshot is loading
 */
export const selectAreSkillSnapshotsLoading = (state: RootState): boolean =>
  Object.values(state.snapshotsSkills?.listStatus).some(
    status => status === ApiStatus.LOADING
  );

/**
 * Retruns true if any snapshot list, create, update, revert or delete in progress
 */
export const selectAreSkillSnapshotsStateLoading = (
  state: RootState
): boolean =>
  Object.values(state.snapshotsSkills?.listStatus).some(
    status => status === ApiStatus.LOADING
  ) ||
  Object.values(state.snapshotsSkills?.createStatus).some(
    status => status === ApiStatus.LOADING
  ) ||
  Object.values(state.snapshotsSkills?.deleteStatus).some(
    status => status === ApiStatus.LOADING
  ) ||
  Object.values(state.snapshotsSkills?.revertStatus).some(
    status => status === ApiStatus.LOADING
  );

export const selectIsSkillSnapshotsLoading = (
  state: RootState,
  skillId: string
): boolean => state.snapshotsSkills?.listStatus[skillId] === ApiStatus.LOADING;

export const selectSnapshotsBySkillId = (
  state: RootState,
  skillId: string
): Snapshot[] => selectSkillSnapshotById(state, skillId)?.snapshots ?? [];

/**
 * Returns the snapshot array corresponding to the currrent value of classicSnapshotSkillId,
 * which is set by listSnapshots and listSnapshotsIfRequired -- can also be set
 * directly by setClassicSnapshotSkillId. This function supports older Classic UI which handles
 * one skill's set of snapshots at a time.
 * @deprecated use {@link selectSnapshotsBySkillId}
 * @param {*} state current store state
 * @returns snapshot array for skill whose id = classicSnapshotSkillId
 */
export const selectClassicSnaphots = (state: RootState): Snapshot[] => {
  if (state.snapshotsSkills?.classicSnapshotSkillId) {
    return selectSnapshotsBySkillId(
      state,
      state.snapshotsSkills.classicSnapshotSkillId
    );
  } else {
    return [];
  }
};

export const selectActionSnapshots = (state: RootState): Snapshot[] => {
  const skillId = selectActionSkillId(state);
  return selectSnapshotsBySkillId(state, skillId);
};

export const selectDialogSnapshots = (state: RootState): Snapshot[] => {
  const skillId = selectDialogSkillId(state);
  return selectSnapshotsBySkillId(state, skillId);
};

export const selectSkillSnapshotCountBySkillId = (
  state: RootState,
  skillId: string
): number => selectSnapshotsBySkillId(state, skillId)?.length || 0;

export const selectSkillSnapshotListError = (
  state: RootState,
  skillId: string
): SerializedError | undefined => state.snapshotsSkills.listError[skillId];
